import { IInvoicesHook } from '@models';
import { InvoiceService } from '@services';
import { useStore } from '@stores';
import { useListSummary } from '../ListSummary';
import { getDateRangeFromPreset } from '@utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useInvoices = (onFiltersChanged?: () => void): IInvoicesHook => {
  const { invoiceStore } = useStore();
  const {
    loading,
    invoices,
    totalCount,
    skipCount,
    maxResultCount,
    currentPage,
    setSkipCount,
    setCurrentPage,
  } = invoiceStore;

  const [number, setNumber] = useState<string>();
  const [customer, setCustomer] = useState<string>();
  const [registrationNumber, setRegistrationNumber] = useState<string>();
  const [status, setStatus] = useState<string[]>();
  const [datePreset, setDatePreset] = useState<string | undefined>();
  const [customFrom, setCustomFrom] = useState<string | undefined>();
  const [customTo, setCustomTo] = useState<string | undefined>();
  const [paymentMethod, setPaymentMethod] = useState<string[]>();
  const [paymentDatePreset, setPaymentDatePreset] = useState<string | undefined>();
  const [paymentCustomFrom, setPaymentCustomFrom] = useState<string | undefined>();
  const [paymentCustomTo, setPaymentCustomTo] = useState<string | undefined>();
  // Bump to re-run the fetch after a mutation (e.g. bulk log payment).
  const [reloadKey, setReloadKey] = useState(0);
  const [sorting, setSorting] = useState<string | undefined>();

  const statusParam = useMemo(() => (status?.length ? status.join(',') : undefined), [status]);
  const paymentMethodParam = useMemo(() => (paymentMethod?.length ? paymentMethod.join(',') : undefined), [paymentMethod]);

  const { startAt, endAt } = useMemo(
    () =>
      datePreset === 'custom'
        ? { startAt: customFrom, endAt: customTo }
        : getDateRangeFromPreset(datePreset),
    [datePreset, customFrom, customTo]
  );

  const { startAt: paymentStartAt, endAt: paymentEndAt } = useMemo(
    () =>
      paymentDatePreset === 'custom'
        ? { startAt: paymentCustomFrom, endAt: paymentCustomTo }
        : getDateRangeFromPreset(paymentDatePreset),
    [paymentDatePreset, paymentCustomFrom, paymentCustomTo]
  );

  const handleStatusChange = (values?: string[]) => {
    setStatus(values?.length ? values : undefined);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handlePaymentMethodChange = (values?: string[]) => {
    setPaymentMethod(values?.length ? values : undefined);
    setSkipCount(0);
    setCurrentPage(1);
  };

  useEffect(() => {
    return () => invoiceStore.resetInvoices();
  }, [invoiceStore]);

  useEffect(() => {
    invoiceStore.getAll({
      customer,
      status: statusParam,
      number,
      registrationNumber,
      startAt,
      endAt,
      paymentMethod: paymentMethodParam,
      paymentStartAt,
      paymentEndAt,
      maxResultCount,
      skipCount,
      sorting,
    });
  }, [
    invoiceStore,
    customer,
    statusParam,
    number,
    registrationNumber,
    startAt,
    endAt,
    paymentMethodParam,
    paymentStartAt,
    paymentEndAt,
    maxResultCount,
    skipCount,
    sorting,
    reloadKey,
  ]);

  // Totals over the whole filtered set: the same filters as the table, none of its paging or sorting.
  // A mutation (reloadKey) can move money between buckets, so it refetches them too.
  const { summary: totals, loading: totalsLoading } = useListSummary(
    () =>
      InvoiceService.getSummary({
        customer,
        status: statusParam,
        number,
        registrationNumber,
        startAt,
        endAt,
        paymentMethod: paymentMethodParam,
        paymentStartAt,
        paymentEndAt,
      }),
    [customer, statusParam, number, registrationNumber, startAt, endAt, paymentMethodParam, paymentStartAt, paymentEndAt, reloadKey]
  );

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  // Clear the selection whenever a filter/search/date changes (skipCount/paging is intentionally
  // excluded). Also fires once on mount, which is a no-op since the selection starts empty.
  useEffect(() => {
    onFiltersChanged?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, statusParam, number, registrationNumber, startAt, endAt, paymentMethodParam, paymentStartAt, paymentEndAt]);

  const handlePageChange = (pagination: any) => {
    invoiceStore.setMaxResultCount(pagination.pageSize);
    invoiceStore.setSkipCount((pagination.current - 1) * pagination.pageSize);
    invoiceStore.setCurrentPage(pagination.current);
  };

  const handleSortChange = (value?: string) => {
    setSorting(value);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleNumberSearch = debounce((text: string) => {
    setNumber(text.trim() || undefined);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleCustomerSearch = debounce((text: string) => {
    setCustomer(text.trim() || undefined);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleRegistrationNumberSearch = debounce((text: string) => {
    setRegistrationNumber(text.trim() || undefined);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset || undefined);
    if (preset !== 'custom') {
      setCustomFrom(undefined);
      setCustomTo(undefined);
    }
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleCustomDateRange = (from?: string, to?: string) => {
    setCustomFrom(from);
    setCustomTo(to);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handlePaymentDatePresetChange = (preset: string) => {
    setPaymentDatePreset(preset || undefined);
    if (preset !== 'custom') {
      setPaymentCustomFrom(undefined);
      setPaymentCustomTo(undefined);
    }
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handlePaymentCustomDateRange = (from?: string, to?: string) => {
    setPaymentCustomFrom(from);
    setPaymentCustomTo(to);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const result = await InvoiceService.getAll({
      customer,
      status: statusParam,
      number,
      registrationNumber,
      startAt,
      endAt,
      paymentMethod: paymentMethodParam,
      paymentStartAt,
      paymentEndAt,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
      sorting,
    });
    return result.items;
  }, [customer, statusParam, number, registrationNumber, startAt, endAt, paymentMethodParam, paymentStartAt, paymentEndAt, totalCount, sorting]);

  return {
    loading,
    dataSource: Array.from(invoices.values()),
    totalCount,
    maxResultCount,
    handleNumberSearch,
    handleCustomerSearch,
    handleRegistrationNumberSearch,
    currentPage,
    setStatus: handleStatusChange,
    datePreset,
    customFrom,
    customTo,
    handleDatePresetChange,
    handleCustomDateRange,
    handlePageChange,
    handleSortChange,
    sorting,
    fetchAllForExport,
    refresh,
    setPaymentMethod: handlePaymentMethodChange,
    paymentDatePreset,
    paymentCustomFrom,
    paymentCustomTo,
    handlePaymentDatePresetChange,
    handlePaymentCustomDateRange,
    totals,
    totalsLoading,
  };
};
